import React, { useState, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  GitMerge,
  Plus,
  ChevronDown,
  ChevronRight,
  Trash2,
  Users,
  Clock,
  AlertCircle,
  CheckCircle2,
  Info,
  Edit2,
  Copy,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useApp } from '@/contexts/AppContext';
import { USER_ROLE_ASSIGNMENTS, ROLE_TEMPLATES } from '@/types/permissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getApprovalWorkflows, createApprovalWorkflow, updateApprovalWorkflow, deleteApprovalWorkflow, createApprovalWorkflowStage, updateApprovalWorkflowStage, deleteApprovalWorkflowStage } from '@/api/services/approvalWorkflowService';
import { getAllUsers, getUsersByPermission } from '@/api/services/userService';
import type { UserListItemApi } from '@/api/models/user';
import { mapWorkflowsFromApi, mapWorkflowFromApi, toCreateWorkflowRequest, toUpdateWorkflowRequest, toCreateStageRequest, toUpdateStageRequest } from '@/api/mappers/approvalWorkflowMapper';
import type { WorkflowDefinition, WorkflowStage, WorkflowAssignee, ApprovalRule, RoutingRule } from '@/api/mappers/approvalWorkflowMapper';

// ─── Demo Data ────────────────────────────────────────────────────────────────

const INITIAL_WORKFLOWS: WorkflowDefinition[] = [
  {
    id: 'wf-standard',
    name: 'Standard Approval',
    description: 'Two-stage review for scenarios up to £500,000 net impact',
    isDefault: true,
    isActive: true,
    routing: {
      minNetImpact: null,
      maxNetImpact: 500000,
      categories: [],
    },
    stages: [
      {
        id: 'stage-1',
        name: 'Finance Review',
        order: 1,
        assignees: [
          {
            id: 'a-1',
            type: 'named',
            userId: '4',
            name: 'Linda Kowalski',
            email: 'linda.kowalski@buildright.co.uk',
          },
        ],
        rule: 'any',
        slaDays: 5,
        escalateToUserId: '1',
        escalateToName: 'Catherine Hughes',
      },
      {
        id: 'stage-2',
        name: 'CEO Sign-off',
        order: 2,
        assignees: [
          {
            id: 'a-2',
            type: 'named',
            userId: '1',
            name: 'Catherine Hughes',
            email: 'catherine.hughes@buildright.co.uk',
          },
        ],
        rule: 'any',
        slaDays: 3,
        escalateToUserId: null,
        escalateToName: null,
      },
    ],
  },
  {
    id: 'wf-highvalue',
    name: 'High Value Approval',
    description: 'Three-stage governance for scenarios above £500,000 net impact',
    isDefault: false,
    isActive: true,
    routing: {
      minNetImpact: 500000,
      maxNetImpact: null,
      categories: [],
    },
    stages: [
      {
        id: 'stage-hv-1',
        name: 'Finance Review',
        order: 1,
        assignees: [
          {
            id: 'a-hv-1',
            type: 'named',
            userId: '4',
            name: 'Linda Kowalski',
            email: 'linda.kowalski@buildright.co.uk',
          },
        ],
        rule: 'any',
        slaDays: 5,
        escalateToUserId: '1',
        escalateToName: 'Catherine Hughes',
      },
      {
        id: 'stage-hv-2',
        name: 'CFO Approval',
        order: 2,
        assignees: [
          {
            id: 'a-hv-2',
            type: 'named',
            userId: '4',
            name: 'Linda Kowalski',
            email: 'linda.kowalski@buildright.co.uk',
          },
        ],
        rule: 'any',
        slaDays: 5,
        escalateToUserId: '1',
        escalateToName: 'Catherine Hughes',
      },
      {
        id: 'stage-hv-3',
        name: 'Board Ratification',
        order: 3,
        assignees: [
          {
            id: 'a-hv-3',
            type: 'named',
            userId: '1',
            name: 'Catherine Hughes',
            email: 'catherine.hughes@buildright.co.uk',
          },
          {
            id: 'a-hv-4',
            type: 'named',
            userId: '8',
            name: "Jennifer O'Sullivan",
            email: 'jennifer.osullivan@boardmember.ie',
          },
        ],
        rule: 'all',
        slaDays: 10,
        escalateToUserId: null,
        escalateToName: null,
      },
    ],
  },
  {
    id: 'wf-crisis',
    name: 'Crisis Fast-Track',
    description: 'Expedited single-stage approval for crisis scenarios',
    isDefault: false,
    isActive: true,
    routing: {
      minNetImpact: null,
      maxNetImpact: null,
      categories: ['crisis'],
    },
    stages: [
      {
        id: 'stage-cr-1',
        name: 'Emergency Approval',
        order: 1,
        assignees: [
          {
            id: 'a-cr-1',
            type: 'named',
            userId: '1',
            name: 'Catherine Hughes',
            email: 'catherine.hughes@buildright.co.uk',
          },
        ],
        rule: 'any',
        slaDays: 1,
        escalateToUserId: null,
        escalateToName: null,
      },
    ],
  },
];

// ─── Helper Components ────────────────────────────────────────────────────────

const StageCard: React.FC<{
  stage: WorkflowStage;
  stageNumber: number;
  totalStages: number;
  onEdit: (stage: WorkflowStage) => void;
  onDelete: (stageId: string) => void;
}> = ({ stage, stageNumber, totalStages, onEdit, onDelete }) => (
  <div className="relative flex gap-4">
    {/* Connector line */}
    {stageNumber < totalStages && (
      <div className="absolute left-[19px] top-10 w-0.5 h-full bg-border -bottom-2" />
    )}

    {/* Stage number bubble */}
    <div className="relative flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center text-sm font-semibold text-primary z-10">
      {stageNumber}
    </div>

    {/* Stage content */}
    <div className="flex-1 bg-muted/30 border rounded-lg p-4 mb-4">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{stage.name}</span>
            <Badge variant="outline" className="text-xs">
              {stage.rule === 'any' ? 'Any one approves' : 'All must approve'}
            </Badge>
          </div>
          {stage.slaDays != null && stage.slaDays > 0 && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <Clock className="h-3 w-3" />
              <span>SLA: {stage.slaDays} business {stage.slaDays === 1 ? 'day' : 'days'}</span>
              {stage.escalateToName && (
                <span className="ml-1">→ escalates to {stage.escalateToName}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => onEdit(stage)}
          >
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
            onClick={() => onDelete(stage.id)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Assignees */}
      <div className="flex flex-wrap gap-2">
        {stage.assignees.map((assignee) => (
          <div
            key={assignee.id}
            className="flex items-center gap-1.5 bg-background border rounded-full px-2.5 py-1 text-xs"
          >
            <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-semibold text-primary">
              {assignee.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
            </div>
            <span>{assignee.name}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const RoutingBadges: React.FC<{ routing: RoutingRule }> = ({ routing }) => {
  const parts: string[] = [];

  if (routing.minNetImpact !== null && routing.maxNetImpact !== null) {
    parts.push(`£${(routing.minNetImpact / 1000).toFixed(0)}K – £${(routing.maxNetImpact / 1000).toFixed(0)}K impact`);
  } else if (routing.minNetImpact !== null) {
    parts.push(`Above £${(routing.minNetImpact / 1000).toFixed(0)}K impact`);
  } else if (routing.maxNetImpact !== null) {
    parts.push(`Under £${(routing.maxNetImpact / 1000).toFixed(0)}K impact`);
  }

  if (routing.categories.length > 0) {
    routing.categories.forEach((c) => {
      parts.push(c.charAt(0).toUpperCase() + c.slice(1));
    });
  }

  if (parts.length === 0) return <Badge variant="outline" className="text-xs">All scenarios</Badge>;

  return (
    <div className="flex flex-wrap gap-1">
      {parts.map((p, i) => (
        <Badge key={i} variant="outline" className="text-xs">{p}</Badge>
      ))}
    </div>
  );
};

// ─── Stage Edit Modal ─────────────────────────────────────────────────────────

const StageEditModal: React.FC<{
  stage: WorkflowStage | null;
  open: boolean;
  onClose: () => void;
  onSave: (stage: WorkflowStage) => void;
  userList: { userId: string; name: string; email: string }[];
}> = ({ stage, open, onClose, onSave, userList }) => {
  const [name, setName] = useState(stage?.name ?? '');
  const [rule, setRule] = useState<ApprovalRule>(stage?.rule ?? 'any');
  const [slaDays, setSlaDays] = useState<string>(stage?.slaDays?.toString() ?? '');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [assignees, setAssignees] = useState<WorkflowAssignee[]>(stage?.assignees ?? []);
  const [escalateToUserId, setEscalateToUserId] = useState<string>(stage?.escalateToUserId ?? 'none');

  // Reset when stage changes
  React.useEffect(() => {
    setName(stage?.name ?? '');
    setRule(stage?.rule ?? 'any');
    setSlaDays(stage?.slaDays?.toString() ?? '');
    setAssignees(stage?.assignees ?? []);
    setEscalateToUserId(stage?.escalateToUserId ?? 'none');
    setSelectedUserId('');
  }, [stage]);

  const availableUsers = userList.filter(
    (u) => !assignees.some((a) => a.userId === u.userId)
  );

  const addAssignee = () => {
    if (!selectedUserId) return;
    const user = userList.find((u) => u.userId === selectedUserId);
    if (!user) return;
    setAssignees((prev) => [
      ...prev,
      {
        id: `a-${Date.now()}`,
        type: 'named',
        userId: user.userId,
        name: user.name,
        email: user.email,
      },
    ]);
    setSelectedUserId('');
  };

  const removeAssignee = (userId: string) => {
    setAssignees((prev) => prev.filter((a) => a.userId !== userId));
    if (escalateToUserId === userId) setEscalateToUserId('');
  };

  const handleSave = () => {
    if (!stage) return;
    const resolvedEscalateId = escalateToUserId === 'none' ? null : escalateToUserId;
    const escalateUser = resolvedEscalateId
      ? userList.find((u) => u.userId === resolvedEscalateId)
      : null;
    onSave({
      ...stage,
      name,
      rule,
      assignees,
      slaDays: slaDays ? parseInt(slaDays, 10) : null,
      escalateToUserId: resolvedEscalateId,
      escalateToName: escalateUser?.name ?? null,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Stage</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Stage name */}
          <div className="space-y-1.5">
            <Label>Stage name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Finance Review"
            />
          </div>

          {/* Approval rule */}
          <div className="space-y-1.5">
            <Label>Approval rule</Label>
            <Select value={rule} onValueChange={(v) => setRule(v as ApprovalRule)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any one approver can approve</SelectItem>
                <SelectItem value="all">All approvers must approve</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {rule === 'any'
                ? 'The first person to approve moves the scenario to the next stage.'
                : 'Every assignee must approve before the scenario progresses.'}
            </p>
          </div>

          {/* Assignees */}
          <div className="space-y-1.5">
            <Label>Assignees</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {assignees.map((a) => (
                <div
                  key={a.userId}
                  className="flex items-center gap-1.5 bg-muted border rounded-full px-2.5 py-1 text-xs"
                >
                  <div className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center text-[9px] font-semibold text-primary">
                    {a.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                  </div>
                  <span>{a.name}</span>
                  <button
                    className="ml-0.5 text-muted-foreground hover:text-destructive"
                    onClick={() => removeAssignee(a.userId)}
                  >
                    ×
                  </button>
                </div>
              ))}
              {assignees.length === 0 && (
                <p className="text-xs text-muted-foreground italic">No assignees yet</p>
              )}
            </div>
            <div className="flex gap-2">
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Add person…" />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map((u) => (
                    <SelectItem key={u.userId} value={u.userId}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={addAssignee} disabled={!selectedUserId}>
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* SLA */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Label>SLA (business days)</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-3.5 w-3.5 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    If no action is taken within this many business days, the scenario is
                    escalated to the person you specify below.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex gap-2 items-center">
              <Input
                type="number"
                min={1}
                max={30}
                value={slaDays}
                onChange={(e) => setSlaDays(e.target.value)}
                placeholder="Leave blank for no SLA"
                className="w-40"
              />
              <span className="text-sm text-muted-foreground">days</span>
            </div>
          </div>

          {/* Escalation */}
          {slaDays && (
            <div className="space-y-1.5">
              <Label>Escalate to (if SLA missed)</Label>
              <Select
                value={escalateToUserId}
                onValueChange={setEscalateToUserId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No escalation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No escalation</SelectItem>
                  {userList.map((u) => (
                    <SelectItem key={u.userId} value={u.userId}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={!name || assignees.length === 0}>
            Save stage
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Workflow Card ────────────────────────────────────────────────────────────

const WorkflowCard: React.FC<{
  workflow: WorkflowDefinition;
  onEdit: (wf: WorkflowDefinition) => void;
  onToggleActive: (id: string) => void;
  onDuplicate: (wf: WorkflowDefinition) => void;
  onDelete: (id: string) => void;
  onStageEdit: (wfId: string, stage: WorkflowStage) => void;
  onStageDelete: (wfId: string, stageId: string) => void;
  onAddStage: (wfId: string) => void;
}> = ({
  workflow,
  onEdit,
  onToggleActive,
  onDuplicate,
  onDelete,
  onStageEdit,
  onStageDelete,
  onAddStage,
}) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div
      className={cn(
        'border rounded-xl overflow-hidden',
        !workflow.isActive && 'opacity-60'
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-3 p-4 bg-muted/30">
        <button
          className="text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{workflow.name}</span>
            {workflow.isDefault && (
              <Badge className="text-xs bg-primary/10 text-primary border-primary/20 hover:bg-primary/10">
                Default
              </Badge>
            )}
            {!workflow.isActive && (
              <Badge variant="outline" className="text-xs text-muted-foreground">
                Inactive
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground truncate">{workflow.description}</p>
        </div>

        {/* Routing */}
        <div className="hidden md:block">
          <RoutingBadges routing={workflow.routing} />
        </div>

        {/* Stage count */}
        <div className="text-sm text-muted-foreground whitespace-nowrap">
          {workflow.stages.length} stage{workflow.stages.length !== 1 ? 's' : ''}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5 mr-2">
                  <Switch
                    checked={workflow.isActive}
                    onCheckedChange={() => onToggleActive(workflow.id)}
                    className="scale-75"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {workflow.isActive ? 'Deactivate workflow' : 'Activate workflow'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(workflow)}>
            <Edit2 className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onDuplicate(workflow)}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-destructive hover:text-destructive"
            onClick={() => onDelete(workflow.id)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Stages */}
      {expanded && (
        <div className="p-4 pt-5">
          {workflow.stages.length === 0 ? (
            <p className="text-sm text-muted-foreground italic text-center py-4">
              No stages defined. Add a stage to make this workflow functional.
            </p>
          ) : (
            <div>
              {workflow.stages.map((stage, idx) => (
                <StageCard
                  key={stage.id}
                  stage={stage}
                  stageNumber={idx + 1}
                  totalStages={workflow.stages.length}
                  onEdit={(s) => onStageEdit(workflow.id, s)}
                  onDelete={(sid) => onStageDelete(workflow.id, sid)}
                />
              ))}
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            className="mt-2 gap-1.5"
            onClick={() => onAddStage(workflow.id)}
          >
            <Plus className="h-3.5 w-3.5" />
            Add stage
          </Button>
        </div>
      )}
    </div>
  );
};

// ─── Workflow Settings Modal ──────────────────────────────────────────────────

const WorkflowSettingsModal: React.FC<{
  workflow: WorkflowDefinition | null;
  open: boolean;
  onClose: () => void;
  onSave: (wf: WorkflowDefinition) => void;
  onDefaultToggle: (checked: boolean, setChecked: (v: boolean) => void) => void;
}> = ({ workflow, open, onClose, onSave, onDefaultToggle }) => {
  const [name, setName] = useState(workflow?.name ?? '');
  const [description, setDescription] = useState(workflow?.description ?? '');
  const [isDefault, setIsDefault] = useState(workflow?.isDefault ?? false);
  const [minImpact, setMinImpact] = useState<string>(
    workflow?.routing.minNetImpact != null
      ? (workflow.routing.minNetImpact / 1000).toString()
      : ''
  );
  const [maxImpact, setMaxImpact] = useState<string>(
    workflow?.routing.maxNetImpact != null
      ? (workflow.routing.maxNetImpact / 1000).toString()
      : ''
  );
  const [categories, setCategories] = useState<string[]>(workflow?.routing.categories ?? []);

  React.useEffect(() => {
    setName(workflow?.name ?? '');
    setDescription(workflow?.description ?? '');
    setIsDefault(workflow?.isDefault ?? false);
    setMinImpact(
      workflow?.routing.minNetImpact != null
        ? (workflow.routing.minNetImpact / 1000).toString()
        : ''
    );
    setMaxImpact(
      workflow?.routing.maxNetImpact != null
        ? (workflow.routing.maxNetImpact / 1000).toString()
        : ''
    );
    setCategories(workflow?.routing.categories ?? []);
  }, [workflow]);

  const toggleCategory = (cat: string) => {
    setCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const handleSave = () => {
    if (!workflow) return;
    onSave({
      ...workflow,
      name,
      description,
      isDefault,
      routing: {
        minNetImpact: minImpact ? parseInt(minImpact, 10) * 1000 : null,
        maxNetImpact: maxImpact ? parseInt(maxImpact, 10) * 1000 : null,
        categories,
      },
    });
  };

  const categoryOptions = [
    { value: 'growth', label: 'Growth', colour: 'text-emerald-600' },
    { value: 'cost-cutting', label: 'Cost-Cutting', colour: 'text-blue-600' },
    { value: 'crisis', label: 'Crisis', colour: 'text-red-600' },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Workflow Settings</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Workflow name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of when this workflow applies"
            />
          </div>

          <div className="flex items-center gap-3">
            <Switch checked={isDefault} onCheckedChange={(checked) => onDefaultToggle(checked, setIsDefault)} />
            <div>
              <Label>Set as default workflow</Label>
              <p className="text-xs text-muted-foreground">
                Applies to all scenarios not matched by another routing rule
              </p>
            </div>
          </div>

          {/* Routing thresholds */}
          <div className="space-y-2">
            <Label>Net impact routing (£ thousands)</Label>
            <p className="text-xs text-muted-foreground">
              This workflow applies to scenarios whose net impact falls within this range.
              Leave both blank to match all scenarios.
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 space-y-1">
                <span className="text-xs text-muted-foreground">From (£K)</span>
                <Input
                  type="number"
                  value={minImpact}
                  onChange={(e) => setMinImpact(e.target.value)}
                  placeholder="No minimum"
                />
              </div>
              <span className="text-muted-foreground mt-5">–</span>
              <div className="flex-1 space-y-1">
                <span className="text-xs text-muted-foreground">To (£K)</span>
                <Input
                  type="number"
                  value={maxImpact}
                  onChange={(e) => setMaxImpact(e.target.value)}
                  placeholder="No maximum"
                />
              </div>
            </div>
          </div>

          {/* Category routing */}
          <div className="space-y-2">
            <Label>Apply to specific scenario categories</Label>
            <p className="text-xs text-muted-foreground">
              Leave blank to apply to all categories. Select categories to restrict this workflow.
            </p>
            <div className="flex gap-2">
              {categoryOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleCategory(opt.value)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                    categories.includes(opt.value)
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-muted-foreground hover:border-primary/50'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={!name}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const ApprovalWorkflowsPage: React.FC = () => {
  const { mode } = useApp();
  const queryClient = useQueryClient();

  const invalidateApprovalCaches = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['pendingApprovals'] });
    queryClient.invalidateQueries({ queryKey: ['approvalWorkflow'] });
  }, [queryClient]);
  const [workflows, setWorkflows] = useState<WorkflowDefinition[]>(
    mode === 'demo' ? INITIAL_WORKFLOWS : [],
  );
  const [settingsModal, setSettingsModal] = useState<WorkflowDefinition | null>(null);
  const [stageModal, setStageModal] = useState<{
    wfId: string;
    stage: WorkflowStage;
  } | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiUsers, setApiUsers] = useState<UserListItemApi[]>([]);
  const [approverUsers, setApproverUsers] = useState<UserListItemApi[]>([]);
  const [defaultConfirm, setDefaultConfirm] = useState<{
    currentDefaultName: string;
  } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: 'workflow' | 'stage';
    workflowId: string;
    stageId?: string;
    name: string;
  } | null>(null);

  // Build a unified user list for the stage modal regardless of mode
  // Only include users who have the approve_reject_scenarios permission
  const availableUserList: { userId: string; name: string; email: string }[] =
    mode === 'live'
      ? approverUsers.map((u) => ({ userId: u.userId, name: u.name, email: u.email }))
      : USER_ROLE_ASSIGNMENTS
          .filter((u) => {
            const role = ROLE_TEMPLATES.find((r) => r.id === u.roleId);
            return role?.permissions.includes('approve_reject_scenarios') ?? false;
          })
          .map((u) => ({ userId: u.userId, name: u.name, email: u.email }));

  // ── Data fetching (live mode) ──────────────────────────────────────────

  const fetchWorkflows = useCallback(async () => {
    if (mode !== 'live') return;

    setLoading(true);
    setError(null);
    try {
      const [workflowsResponse, usersResponse, approverUsersResponse] = await Promise.all([
        getApprovalWorkflows(),
        getAllUsers(),
        getUsersByPermission('approve_reject_scenarios'),
      ]);
      setApiUsers(usersResponse.users);
      setApproverUsers(approverUsersResponse.users);
      setWorkflows(mapWorkflowsFromApi(workflowsResponse, usersResponse.users));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    if (mode === 'live') {
      fetchWorkflows();
    } else {
      setWorkflows(INITIAL_WORKFLOWS);
    }
  }, [fetchWorkflows, mode]);

  // ── Workflow actions ─────────────────────────────────────────────────────

  const handleToggleActive = async (id: string) => {
    const wf = workflows.find((w) => w.id === id);
    if (!wf) return;

    const toggled = { ...wf, isActive: !wf.isActive };
    setWorkflows((prev) =>
      prev.map((w) => (w.id === id ? toggled : w))
    );

    if (mode === 'live') {
      try {
        await updateApprovalWorkflow(Number(id), toUpdateWorkflowRequest(toggled));
        invalidateApprovalCaches();
      } catch (err) {
        console.error('Failed to toggle workflow active state:', err);
        setWorkflows((prev) =>
          prev.map((w) => (w.id === id ? wf : w))
        );
      }
    }
  };

  const handleDuplicate = async (wf: WorkflowDefinition) => {
    const copyDef: WorkflowDefinition = {
      ...wf,
      id: `wf-${Date.now()}`,
      name: `${wf.name} (Copy)`,
      isDefault: false,
      stages: wf.stages.map((s) => ({
        ...s,
        id: `stage-${Date.now()}-${s.order}`,
        assignees: s.assignees.map((a) => ({ ...a, id: `a-${Date.now()}-${a.userId}` })),
      })),
    };

    if (mode === 'live') {
      try {
        const created = await createApprovalWorkflow(toCreateWorkflowRequest(copyDef));
        for (const stage of wf.stages) {
          await createApprovalWorkflowStage(created.id, toCreateStageRequest(stage));
        }
        const refreshed = await getApprovalWorkflows();
        setWorkflows(mapWorkflowsFromApi(refreshed, apiUsers));
      } catch (err) {
        console.error('Failed to duplicate workflow:', err);
      }
    } else {
      setWorkflows((prev) => [...prev, copyDef]);
    }
  };

  const handleDeleteRequest = (id: string) => {
    const wf = workflows.find((w) => w.id === id);
    if (!wf) return;
    setDeleteConfirm({ type: 'workflow', workflowId: id, name: wf.name });
  };

  const handleDelete = async (id: string) => {
    setWorkflows((prev) => prev.filter((wf) => wf.id !== id));

    if (mode === 'live') {
      try {
        await deleteApprovalWorkflow(Number(id));
        invalidateApprovalCaches();
      } catch (err) {
        console.error('Failed to delete workflow:', err);
        await fetchWorkflows();
      }
    }
  };

  // Ref to the modal's setIsDefault so the confirmation dialog can update it
  const modalSetIsDefaultRef = React.useRef<((v: boolean) => void) | null>(null);

  const handleDefaultToggle = (checked: boolean, setChecked: (v: boolean) => void) => {
    if (!settingsModal) return;

    if (checked) {
      const currentDefault = workflows.find((w) => w.isDefault && w.id !== settingsModal.id);
      if (currentDefault) {
        modalSetIsDefaultRef.current = setChecked;
        setDefaultConfirm({ currentDefaultName: currentDefault.name });
      } else {
        setChecked(true);
      }
    } else {
      setChecked(false);
    }
  };

  const confirmSwitchDefault = async () => {
    if (!settingsModal) return;

    const currentDefault = workflows.find((w) => w.isDefault && w.id !== settingsModal.id);
    if (currentDefault) {
      const unsetDefault = { ...currentDefault, isDefault: false };
      setWorkflows((prev) =>
        prev.map((wf) => (wf.id === currentDefault.id ? unsetDefault : wf))
      );

      if (mode === 'live') {
        try {
          await updateApprovalWorkflow(Number(currentDefault.id), toUpdateWorkflowRequest(unsetDefault));
        } catch (err) {
          console.error('Failed to unset previous default workflow:', err);
          await fetchWorkflows();
        }
      }
    }

    modalSetIsDefaultRef.current?.(true);
    modalSetIsDefaultRef.current = null;
    setDefaultConfirm(null);
  };

  const handleSaveSettings = async (updated: WorkflowDefinition) => {
    setWorkflows((prev) =>
      prev.map((wf) => (wf.id === updated.id ? updated : wf))
    );
    setSettingsModal(null);

    if (mode === 'live') {
      try {
        await updateApprovalWorkflow(Number(updated.id), toUpdateWorkflowRequest(updated));
        invalidateApprovalCaches();
      } catch (err) {
        console.error('Failed to update workflow settings:', err);
        await fetchWorkflows();
      }
    }
  };

  const handleNewWorkflow = async () => {
    const blank: WorkflowDefinition = {
      id: `wf-${Date.now()}`,
      name: 'New Workflow',
      description: '',
      isDefault: false,
      isActive: true,
      routing: { minNetImpact: null, maxNetImpact: null, categories: [] },
      stages: [],
    };

    if (mode === 'live') {
      try {
        const created = await createApprovalWorkflow(toCreateWorkflowRequest(blank));
        const mapped = mapWorkflowFromApi(created, apiUsers);
        setWorkflows((prev) => [...prev, mapped]);
        setSettingsModal(mapped);
      } catch (err) {
        console.error('Failed to create workflow:', err);
      }
    } else {
      setWorkflows((prev) => [...prev, blank]);
      setSettingsModal(blank);
    }
  };

  // ── Stage actions ────────────────────────────────────────────────────────

  const handleAddStage = (wfId: string) => {
    const wf = workflows.find((w) => w.id === wfId);
    if (!wf) return;
    const newStage: WorkflowStage = {
      id: `stage-${Date.now()}`,
      name: `Stage ${wf.stages.length + 1}`,
      order: wf.stages.length + 1,
      assignees: [],
      rule: 'any',
      slaDays: null,
      escalateToUserId: null,
      escalateToName: null,
    };
    setStageModal({ wfId, stage: newStage });
  };

  const handleStageEdit = (wfId: string, stage: WorkflowStage) => {
    setStageModal({ wfId, stage });
  };

  const handleStageDeleteRequest = (wfId: string, stageId: string) => {
    const wf = workflows.find((w) => w.id === wfId);
    const stage = wf?.stages.find((s) => s.id === stageId);
    if (!wf || !stage) return;
    setDeleteConfirm({ type: 'stage', workflowId: wfId, stageId, name: stage.name });
  };

  const handleStageDelete = async (wfId: string, stageId: string) => {
    setWorkflows((prev) =>
      prev.map((wf) => {
        if (wf.id !== wfId) return wf;
        const updated = wf.stages
          .filter((s) => s.id !== stageId)
          .map((s, idx) => ({ ...s, order: idx + 1 }));
        return { ...wf, stages: updated };
      })
    );

    if (mode === 'live') {
      try {
        await deleteApprovalWorkflowStage(Number(wfId), Number(stageId));
        invalidateApprovalCaches();
      } catch (err) {
        console.error('Failed to delete stage:', err);
        await fetchWorkflows();
      }
    }
  };

  const handleSaveStage = async (stage: WorkflowStage) => {
    if (!stageModal) return;
    const { wfId } = stageModal;

    if (mode === 'live') {
      try {
        const wf = workflows.find((w) => w.id === wfId);
        const existing = wf?.stages.find((s) => s.id === stage.id);
        if (existing) {
          await updateApprovalWorkflowStage(
            Number(wfId),
            Number(stage.id),
            toUpdateStageRequest(stage),
          );
        } else {
          await createApprovalWorkflowStage(
            Number(wfId),
            toCreateStageRequest(stage),
          );
        }
        await fetchWorkflows();
        invalidateApprovalCaches();
      } catch (err) {
        console.error('Failed to save stage:', err);
      }
    } else {
      setWorkflows((prev) =>
        prev.map((wf) => {
          if (wf.id !== wfId) return wf;
          const existingStage = wf.stages.find((s) => s.id === stage.id);
          let updated: WorkflowStage[];
          if (existingStage) {
            updated = wf.stages.map((s) => (s.id === stage.id ? stage : s));
          } else {
            updated = [...wf.stages, stage];
          }
          return { ...wf, stages: updated };
        })
      );
    }
    setStageModal(null);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading workflows...</span>
      </div>
    );
  }

  if (error && mode === 'live') {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-destructive">
              Error Loading Workflows
            </CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={fetchWorkflows}>Retry</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const activeCount = workflows.filter((w) => w.isActive).length;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <GitMerge className="h-5 w-5 text-primary" />
            <h1 className="text-xl font-semibold">Approval Workflows</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Configure multi-stage approval processes for scenario planning. Scenarios are routed to
            the appropriate workflow based on their net impact and category.
          </p>
        </div>
        <Button onClick={handleNewWorkflow} className="gap-1.5 flex-shrink-0">
          <Plus className="h-4 w-4" />
          New workflow
        </Button>
      </div>

      {/* Info banner */}
      <div className="flex gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-sm text-blue-800 dark:text-blue-300">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div>
          <span className="font-medium">How routing works — </span>
          when a scenario is submitted for approval, the system finds the first active workflow whose
          routing rules match. If no specific workflow matches, the{' '}
          <span className="font-medium">default workflow</span> is used. Crisis category scenarios
          always match any workflow with Crisis routing, regardless of impact threshold.
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          {
            label: 'Active workflows',
            value: activeCount,
            icon: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
          },
          {
            label: 'Total stages configured',
            value: workflows.reduce((sum, wf) => sum + wf.stages.length, 0),
            icon: <GitMerge className="h-4 w-4 text-primary" />,
          },
          {
            label: 'Named approvers',
            value: new Set(
              workflows.flatMap((wf) =>
                wf.stages.flatMap((s) => s.assignees.map((a) => a.userId))
              )
            ).size,
            icon: <Users className="h-4 w-4 text-violet-500" />,
          },
        ].map((stat) => (
          <div key={stat.label} className="border rounded-lg p-3 flex items-center gap-3">
            {stat.icon}
            <div>
              <div className="text-xl font-semibold">{stat.value}</div>
              <div className="text-xs text-muted-foreground">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Workflow cards */}
      <div className="space-y-4">
        {workflows.length === 0 ? (
          <div className="border-2 border-dashed rounded-xl p-12 text-center">
            <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium text-muted-foreground">No workflows configured</p>
            <p className="text-sm text-muted-foreground mt-1">
              Without a workflow, scenarios can be marked Active directly by their creator.
            </p>
            <Button onClick={handleNewWorkflow} className="mt-4 gap-1.5">
              <Plus className="h-4 w-4" />
              Create your first workflow
            </Button>
          </div>
        ) : (
          workflows.map((wf) => (
            <WorkflowCard
              key={wf.id}
              workflow={wf}
              onEdit={setSettingsModal}
              onToggleActive={handleToggleActive}
              onDuplicate={handleDuplicate}
              onDelete={handleDeleteRequest}
              onStageEdit={handleStageEdit}
              onStageDelete={handleStageDeleteRequest}
              onAddStage={handleAddStage}
            />
          ))
        )}
      </div>

      {/* Workflow settings modal */}
      <WorkflowSettingsModal
        workflow={settingsModal}
        open={settingsModal !== null}
        onClose={() => setSettingsModal(null)}
        onSave={handleSaveSettings}
        onDefaultToggle={handleDefaultToggle}
      />

      {/* Change default workflow confirmation dialog */}
      <Dialog open={defaultConfirm !== null} onOpenChange={() => setDefaultConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Default Workflow?</DialogTitle>
            <DialogDescription>
              {defaultConfirm?.currentDefaultName} is currently set as the default workflow.
              Do you want to switch to {settingsModal?.name} instead?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDefaultConfirm(null)}>
              Cancel
            </Button>
            <Button onClick={confirmSwitchDefault}>
              Switch to {settingsModal?.name}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteConfirm !== null} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteConfirm?.type === 'workflow' ? 'Workflow' : 'Stage'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>"{deleteConfirm?.name}"</strong>?{' '}
              {deleteConfirm?.type === 'workflow'
                ? 'This will remove the workflow and all its stages. '
                : 'This will remove the stage from the workflow. '}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirm(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!deleteConfirm) return;
                if (deleteConfirm.type === 'workflow') {
                  handleDelete(deleteConfirm.workflowId);
                } else if (deleteConfirm.stageId) {
                  handleStageDelete(deleteConfirm.workflowId, deleteConfirm.stageId);
                }
                setDeleteConfirm(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stage edit modal */}
      <StageEditModal
        stage={stageModal?.stage ?? null}
        open={stageModal !== null}
        onClose={() => setStageModal(null)}
        onSave={handleSaveStage}
        userList={availableUserList}
      />
    </div>
  );
};

export default ApprovalWorkflowsPage;
